import { DriveStep, PopoverDOM } from 'driver.js';

import { addTourStopEventListeners } from '../public-api';
import { projectDisplayText } from '../utils/project-display-text';
import { TourStop } from './tour-stop';
import { TourStopButtonAction } from './tour-stop-button-action';
import { TourStopKind } from './tour-stop-kind';
import { tourStopPoiTemplate } from './tour-stop-poi-template';

/** Template key for the title text */
const TITLE_KEY = 'title';
/** Template key for the description text */
const DESCRIPTION_KEY = 'description';
/** Template key for the next button action */
const NEXT_BUTTON_KEY = 'nextButton';
/** Template key for the end button action */
const END_BUTTON_KEY = 'endButton';

/**
 * A point-of-interest tour stop that highlights specific UI elements.
 *
 * TourStopPoi is used to draw attention to particular elements on the page
 * by displaying a popover with title, description, and action buttons.
 * It uses the driver.js library to create an overlay that highlights the
 * target element while dimming the rest of the page.
 *
 * @example
 * ```typescript
 * const poiStop = new TourStopPoi(
 *   '#my-button',
 *   'Welcome to the Action Button',
 *   'Click this button to perform the main action',
 *   {
 *     nextButton: {
 *       displayText: 'Got it!',
 *       listener: () => tourService.nextStop()
 *     },
 *     endButton: {
 *       displayText: 'Skip Tour',
 *       listener: () => tourService.endTour()
 *     }
 *   },
 *   { side: 'bottom', align: 'center' }
 * );
 * ```
 */
export class TourStopPoi extends TourStop {
  /** @inheritdoc */
  override kind: TourStopKind = 'poi';

  /**
   * Creates a new point-of-interest tour stop.
   *
   * @param element - CSS selector, DOM element, or driver.js element specification for the target
   * @param title - The title text to display in the popover
   * @param description - The description text to display in the popover
   * @param buttonActions - Configuration for interactive buttons in the popover
   * @param buttonActions.nextButton - Optional configuration for the next/continue button
   * @param buttonActions.endButton - Optional configuration for the end/skip button
   * @param position - Optional positioning configuration for the popover
   * @param position.side - Which side of the target element to show the popover
   * @param position.align - How to align the popover relative to the target element
   *
   * @example
   * ```typescript
   * // Simple POI with just a next button
   * const simpleStop = new TourStopPoi(
   *   '#target-element',
   *   'Step Title',
   *   'Step description explaining what to do',
   *   {
   *     nextButton: {
   *       displayText: 'Continue',
   *       listener: () => tourService.nextStop()
   *     }
   *   }
   * );
   *
   * // POI with both next and skip buttons, positioned above the target
   * const fullStop = new TourStopPoi(
   *   '#target-element',
   *   'Important Feature',
   *   'This feature helps you accomplish your goals',
   *   {
   *     nextButton: {
   *       displayText: 'Show me more',
   *       listener: () => tourService.nextStop()
   *     },
   *     endButton: {
   *       displayText: 'Skip tour',
   *       listener: () => tourService.endTour()
   *     }
   *   },
   *   { side: 'top', align: 'center' }
   * );
   * ```
   */
  constructor(
    public element: DriveStep['element'],
    public title: string,
    public description: string,
    buttonActions: {
      endButton?: TourStopButtonAction;
      nextButton?: TourStopButtonAction;
    },
    position?: {
      align?: 'center' | 'end' | 'start';
      side?: 'bottom' | 'left' | 'right' | 'top';
    }
  ) {
    super();

    // Set up display text for template interpolation
    this.displayText[TITLE_KEY] = this.title;
    this.displayText[DESCRIPTION_KEY] = this.description;

    // Configure button actions if provided
    if (buttonActions.nextButton) {
      this.buttonActions[NEXT_BUTTON_KEY] = buttonActions.nextButton;
    }

    if (buttonActions.endButton) {
      this.buttonActions[END_BUTTON_KEY] = buttonActions.endButton;
    }

    // Set positioning if provided
    if (position) {
      this.position = position;
    }
  }

  /**
   * Converts this POI stop to a driver.js step configuration.
   *
   * This method creates a driver.js step that will highlight the target element
   * and display a popover with the configured title, description, and buttons.
   * The popover content is generated from the POI template with text interpolation.
   *
   * @returns A driver.js step configuration object
   *
   * @example
   * ```typescript
   * const driverStep = poiStop.toDriverJsStep();
   * // Results in a driver.js step with:
   * // - element: the target element selector
   * // - popover: configured with title, description, and buttons
   * // - positioning: based on the provided position configuration
   * ```
   */
  toDriverJsStep() {
    // Generate the popover content by interpolating the template
    const description = projectDisplayText(
      tourStopPoiTemplate,
      this.displayText
    );

    // Set up event listeners for interactive buttons
    const onPopoverRender = ({ wrapper }: PopoverDOM) => {
      addTourStopEventListeners(this.buttonActions, wrapper);
    };

    return {
      element: this.element,
      popover: {
        description,
        side: this.position?.side,
        align: this.position?.align,
        showButtons: [], // Hide default driver.js buttons since we use custom ones
        onPopoverRender,
      },
    } as DriveStep;
  }
}
